"use client";

import { useState } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import styles from "./ConfirmDeleteModal.module.css";

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: string;
  itemName?: string;
  itemCount?: number;
  isLoading?: boolean;
  confirmText?: string;
  cancelText?: string;
}

export default function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Xác nhận xóa",
  message = "Bạn có chắc chắn muốn xóa mục này?",
  itemName,
  itemCount,
  isLoading = false,
  confirmText = "Xóa",
  cancelText = "Hủy"
}: ConfirmDeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <AlertTriangle size={24} className={styles.warningIcon} />
          </div>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.content}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.message}>
            {itemCount && itemCount > 1 
              ? `Bạn có chắc chắn muốn xóa vĩnh viễn ${itemCount} mục?`
              : message
            }
            {itemName && !itemCount && (
              <span className={styles.itemName}> "{itemName}"</span>
            )}
          </p>
          <p className={styles.warning}>
            Hành động này không thể hoàn tác.
          </p>
        </div>

        <div className={styles.footer}>
          <button 
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isDeleting}
          >
            {cancelText}
          </button>
          <button 
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={isDeleting || isLoading}
          >
            {isDeleting || isLoading ? (
              <>
                <Loader2 size={16} className={styles.spinner} />
                Đang xóa...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
